import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

const DatenschutzPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <article data-testid="datenschutz-content">
          {/* Title */}
          <div className="mb-8 pb-6 border-b border-border">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-['Oswald'] font-bold tracking-wider uppercase m-0">
                Datenschutzerkl&auml;rung
              </h1>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Gem&auml;ss dem revidierten Datenschutzgesetz (DSG) der Schweiz
            </p>
          </div>

          <div className="space-y-8 text-foreground/90">
            {/* 1. Allgemeine Hinweise */}
            <section>
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4 text-primary">
                1. Allgemeine Hinweise
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="leading-relaxed">
                  Diese Datenschutzerkl&auml;rung informiert Sie dar&uuml;ber, wie wir auf der Website{' '}
                  <a href="https://www.shrt.ch" className="text-primary hover:underline">www.shrt.ch</a>{' '}
                  sowie im zugeh&ouml;rigen Mitgliederportal Personendaten erheben, verarbeiten und speichern.
                  Der Schutz Ihrer Daten ist uns ein wichtiges Anliegen. Wir behandeln Ihre Personendaten
                  vertraulich und gem&auml;ss den geltenden Datenschutzvorschriften, insbesondere dem{' '}
                  <strong>revidierten Datenschutzgesetz (DSG) der Schweiz</strong>.
                </p>
              </div>
            </section>

            {/* 2. Verantwortliche Stelle */}
            <section>
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4 text-primary">
                2. Verantwortliche Stelle
              </h2>
              <div className="bg-card border border-border p-6">
                <p className="font-bold text-lg mb-1">S.H.R.T.</p>
                <p className="text-muted-foreground mb-1">Swiss Historic Racing Team</p>
                <p className="text-sm text-muted-foreground mb-3">Verein gem&auml;ss Art. 60 ff. ZGB</p>
                <p>Kuno Sch&auml;r</p>
                <p>St. Niklausstrasse 1</p>
                <p>CH-4502 Solothurn</p>
                <p className="mt-3">
                  E-Mail:{' '}
                  <a href="mailto:info@kunoschaer.ch" className="text-primary hover:underline">info@kunoschaer.ch</a>
                </p>
                <p>
                  E-Mail Datenschutz:{' '}
                  <a href="mailto:info@shrt.ch" className="text-primary hover:underline">info@shrt.ch</a>
                </p>
                <p>
                  Telefon:{' '}
                  <a href="tel:+41794153031" className="text-primary hover:underline">+41 79 415 30 31</a>
                </p>
                <p className="mt-3 pt-3 border-t border-border">
                  Technische Anliegen: Thomas Barth{' '}
                  <a href="mailto:barththomas@mac.com" className="text-primary hover:underline">barththomas@mac.com</a>
                </p>
              </div>
            </section>

            {/* 3. Erhebung und Zweck */}
            <section>
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4 text-primary">
                3. Erhebung und Zweck der Datenbearbeitung
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="leading-relaxed mb-3">
                  Wir bearbeiten Personendaten nur, soweit dies f&uuml;r den Betrieb unserer Vereins-Website
                  und des Mitgliederportals erforderlich ist. Folgende Daten werden typischerweise verarbeitet:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
                  <li>IP-Adresse und technische Metadaten beim Besuch der Website (Logfiles)</li>
                  <li>Kontaktinformationen bei Anfragen per E-Mail oder Kontaktformular</li>
                  <li>Mitgliederdaten (Name, Adresse, E-Mail, Telefon) zur Vereinsverwaltung</li>
                  <li>Fahrzeugdaten und Bilder f&uuml;r die Fahrzeuggalerie</li>
                  <li>Anmeldedaten f&uuml;r Veranstaltungen</li>
                  <li>Zahlungsdaten im Rahmen der Rechnungsstellung und Beitragszahlung</li>
                  <li>Anonyme Umfrageantworten zur Verbesserung des Vereinsangebots</li>
                </ul>
                <p className="leading-relaxed">Die Datenbearbeitung erfolgt zu folgenden Zwecken:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Technischer Betrieb und Sicherheit der Website und des Mitgliederportals</li>
                  <li>Verwaltung der Vereinsmitgliedschaft</li>
                  <li>Rechnungsstellung und Zahlungsabwicklung</li>
                  <li>Organisation und Durchf&uuml;hrung von Veranstaltungen</li>
                  <li>Versand von Vereinsnewslettern und -mitteilungen</li>
                  <li>Beantwortung von Nutzeranfragen</li>
                </ul>
              </div>
            </section>

            {/* 4. Hosting */}
            <section>
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4 text-primary">
                4. Hosting und Server-Logfiles
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="leading-relaxed mb-3">
                  Unsere Website wird bei einem professionellen Hostinganbieter betrieben.
                  Bei jedem Zugriff werden automatisch Informationen in sogenannten Server-Logfiles
                  gespeichert. Dies betrifft insbesondere:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>IP-Adresse</li>
                  <li>Datum und Uhrzeit</li>
                  <li>Browsertyp und -version</li>
                  <li>Betriebssystem</li>
                  <li>Referrer-URL</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  Diese Daten dienen ausschliesslich der Systemsicherheit und werden nicht mit
                  anderen Datenquellen zusammengef&uuml;hrt.
                </p>
              </div>
            </section>

            {/* 5. Cookies */}
            <section>
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4 text-primary">
                5. Cookies
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="leading-relaxed">
                  Unsere Website verwendet <strong>keine Cookies</strong> zur Nutzerverfolgung oder
                  Personalisierung. F&uuml;r das Mitgliederportal wird ein sogenanntes Session-Token
                  im lokalen Speicher des Browsers abgelegt, um die Anmeldung aufrechtzuerhalten.
                  Dieses Token wird bei der Abmeldung gel&ouml;scht.
                </p>
              </div>
            </section>

            {/* 6. Zahlungsabwicklung - KEY SECTION FOR STRIPE */}
            <section>
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4 text-primary">
                6. Zahlungsabwicklung
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="leading-relaxed mb-3">
                  F&uuml;r die Abwicklung von Mitgliedsbeitr&auml;gen und weiteren Zahlungen nutzen wir den
                  Zahlungsdienstleister <strong>Stripe, Inc.</strong> (510 Townsend Street, San Francisco,
                  CA 94103, USA / Stripe Payments Europe, Ltd., 1 Grand Canal Street Lower, Dublin 2, Irland).
                </p>
                <p className="leading-relaxed mb-3">
                  Die Verarbeitung von <strong>Visa- und Mastercard-Transaktionen</strong> erfolgt durch{' '}
                  <strong>Stripe Payments UK Ltd (SPUKL)</strong>, 211 Old Street, The Warehouse, 7th Floor,
                  London EC1V 9NR, Vereinigtes K&ouml;nigreich, als zugelassener Acquirer der Kartennetzwerke
                  Visa und Mastercard. SPUKL unterliegt den Regelwerken dieser Kartennetzwerke (Network Rules)
                  sowie den Anforderungen der PCI DSS (Payment Card Industry Data Security Standards).
                </p>
                <p className="leading-relaxed mb-3">
                  Stripe erm&ouml;glicht uns die Annahme von Zahlungen per{' '}
                  <strong>Kreditkarte</strong> (Visa, Mastercard) und <strong>TWINT</strong>.
                </p>
                <p className="leading-relaxed mb-3">
                  Bei einer Zahlung werden folgende Daten an Stripe bzw. SPUKL &uuml;bermittelt:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
                  <li>Zahlungsbetrag und W&auml;hrung</li>
                  <li>Zahlungsmethode (Kreditkartendaten bzw. TWINT-Referenz)</li>
                  <li>Name und E-Mail-Adresse des Zahlungspflichtigen</li>
                  <li>Mobiltelefonnummer des Zahlungspflichtigen</li>
                  <li>Rechnungsnummer und Verwendungszweck</li>
                </ul>
                <p className="leading-relaxed mb-3">
                  <strong>Erhebung der Mobiltelefonnummer:</strong> Bei Zahlungen &uuml;ber unser
                  Online-Zahlungsportal wird Ihre Mobiltelefonnummer erhoben. Dies ist insbesondere
                  f&uuml;r die Nutzung von TWINT als Zahlungsmethode erforderlich. Gem&auml;ss den
                  TWINT-Zahlungsbedingungen sind wir verpflichtet, die Mobiltelefonnummer des Kunden
                  zu erfassen, zu verifizieren und auf Anfrage an Stripe bzw. TWINT weiterzugeben.
                </p>
                <p className="leading-relaxed mb-3">
                  Stripe und SPUKL verarbeiten diese Daten zur Zahlungsabwicklung, Betrugserkennung und
                  Erf&uuml;llung gesetzlicher Pflichten. Die Verarbeitung erfolgt auf Grundlage
                  der Vertragserf&uuml;llung (Art. 6 Abs. 1 lit. b DSG) sowie unserer berechtigten
                  Interessen an einer sicheren und effizienten Zahlungsabwicklung.
                </p>
                <p className="leading-relaxed mb-3">
                  <strong>Datenweitergabe im Rahmen der Kartenzahlung:</strong> Stripe und SPUKL sind
                  berechtigt, Ihre Transaktionsdaten an die Kartennetzwerke (Visa, Mastercard), an Betrugs-
                  und Geldw&auml;schepr&uuml;fungsstellen sowie an zust&auml;ndige Beh&ouml;rden
                  weiterzugeben, soweit dies zur Erbringung der Zahlungsdienste, zur Einhaltung gesetzlicher
                  und regulatorischer Pflichten oder zur Risiko- und Betrugspr&uuml;fung erforderlich ist.
                </p>
                <p className="leading-relaxed mb-3">
                  <strong>Kreditkartendaten</strong> werden ausschliesslich von Stripe bzw. SPUKL erfasst
                  und PCI-DSS-konform verarbeitet. Wir haben zu keinem Zeitpunkt Zugang zu vollst&auml;ndigen
                  Kreditkartennummern. Wir erhalten von Stripe lediglich eine Best&auml;tigung
                  &uuml;ber den Zahlungsstatus.
                </p>
                <p className="leading-relaxed mb-3">
                  Bei Zahlungen per <strong>TWINT</strong> werden Sie zur Autorisierung an die
                  TWINT-App weitergeleitet. Die Verarbeitung der TWINT-Transaktionsdaten erfolgt
                  durch TWINT AG und Stripe gem&auml;ss deren Datenschutzbestimmungen. Der Firmenname
                  &laquo;S.H.R.T. &ndash; Swiss Historic Racing Team&raquo; wird in allen
                  zahlungsrelevanten Informationen (Rechnungen, Best&auml;tigungen) angegeben.
                </p>
                <p className="leading-relaxed mb-3">
                  Stripe und SPUKL k&ouml;nnen Daten in die USA bzw. das Vereinigte K&ouml;nigreich
                  &uuml;bermitteln. Stripe ist unter dem EU-U.S. Data Privacy Framework zertifiziert
                  und gew&auml;hrleistet ein angemessenes Datenschutzniveau.
                </p>
                <p className="leading-relaxed mb-2">Weitere Informationen:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>
                    Datenschutz bei Stripe:{' '}
                    <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      https://stripe.com/privacy
                    </a>
                  </li>
                  <li>
                    TWINT-Zahlungsbedingungen:{' '}
                    <a href="https://stripe.com/de-ch/legal/twint" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      https://stripe.com/de-ch/legal/twint
                    </a>
                  </li>
                  <li>
                    SPUKL Acquirer Terms (Visa/Mastercard):{' '}
                    <a href="https://stripe.com/de-ch/legal/spukl-acquirer-terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      https://stripe.com/de-ch/legal/spukl-acquirer-terms
                    </a>
                  </li>
                </ul>
              </div>
            </section>

            {/* 7. E-Mail-Versand */}
            <section>
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4 text-primary">
                7. E-Mail-Versand und Newsletter
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="leading-relaxed mb-3">
                  F&uuml;r den Versand von Vereinsmitteilungen, Rechnungen und Newslettern verwenden wir
                  den E-Mail-Dienst <strong>Resend</strong> (Resend, Inc., San Francisco, USA).
                  Dabei werden Ihre E-Mail-Adresse und Ihr Name an Resend &uuml;bermittelt, um die
                  Zustellung zu erm&ouml;glichen.
                </p>
                <p className="leading-relaxed">
                  Weitere Informationen:{' '}
                  <a
                    href="https://resend.com/legal/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    https://resend.com/legal/privacy-policy
                  </a>
                </p>
              </div>
            </section>

            {/* 8. Cloud-Speicher */}
            <section>
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4 text-primary">
                8. Cloud-Speicher f&uuml;r Dateien und Bilder
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="leading-relaxed">
                  Hochgeladene Bilder (z.B. Fahrzeugfotos) und Dokumente werden bei{' '}
                  <strong>Cloudflare R2</strong> gespeichert. Cloudflare verarbeitet die Daten
                  zur Bereitstellung des Speicherdienstes. Weitere Informationen:{' '}
                  <a
                    href="https://www.cloudflare.com/privacypolicy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    https://www.cloudflare.com/privacypolicy/
                  </a>
                </p>
              </div>
            </section>

            {/* 9. Weitergabe */}
            <section>
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4 text-primary">
                9. Weitergabe von Daten an Dritte
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="leading-relaxed">
                  Eine Weitergabe Ihrer Personendaten an Dritte erfolgt nur in den oben beschriebenen
                  F&auml;llen (Zahlungsabwicklung, E-Mail-Versand, Cloud-Speicher) oder wenn dies
                  gesetzlich vorgeschrieben ist. Eine dar&uuml;ber hinausgehende Weitergabe findet
                  nicht statt.
                </p>
              </div>
            </section>

            {/* 10. Rechte */}
            <section>
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4 text-primary">
                10. Rechte der betroffenen Personen
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="leading-relaxed mb-3">
                  Sie haben gem&auml;ss Datenschutzgesetz das Recht auf:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Auskunft &uuml;ber Ihre gespeicherten Daten</li>
                  <li>Berichtigung unrichtiger Daten</li>
                  <li>L&ouml;schung Ihrer Daten (sofern keine gesetzlichen Aufbewahrungspflichten bestehen)</li>
                  <li>Einschr&auml;nkung der Verarbeitung</li>
                  <li>Daten&uuml;bertragbarkeit</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  Zur Aus&uuml;bung Ihrer Rechte wenden Sie sich bitte an die oben genannte
                  verantwortliche Stelle.
                </p>
              </div>
            </section>

            {/* 11. Datensicherheit */}
            <section>
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4 text-primary">
                11. Datensicherheit
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="leading-relaxed">
                  Wir setzen technische und organisatorische Sicherheitsmassnahmen ein, um Ihre
                  Personendaten vor Verlust, Missbrauch und unbefugtem Zugriff zu sch&uuml;tzen.
                  Die Daten&uuml;bertragung erfolgt verschl&uuml;sselt &uuml;ber HTTPS.
                  Passw&ouml;rter werden ausschliesslich in gehashter Form gespeichert.
                </p>
              </div>
            </section>

            {/* 12. Aenderungen */}
            <section>
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4 text-primary">
                12. &Auml;nderungen
              </h2>
              <div className="prose prose-invert max-w-none">
                <p className="leading-relaxed">
                  Wir behalten uns vor, diese Datenschutzerkl&auml;rung jederzeit zu &auml;ndern.
                  Es gilt jeweils die auf dieser Website publizierte Version.
                </p>
                <p className="leading-relaxed mt-4 text-muted-foreground">
                  <strong>Stand:</strong> M&auml;rz 2026
                </p>
              </div>
            </section>
          </div>
        </article>
      </main>

      <PublicFooter />
    </div>
  );
};

export default DatenschutzPage;
